import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { PageHeader } from '../../components/layout';
import notificationService, {
  NotificationsCenter,
  NotificationListItem,
} from '../../services/api/notificationService';
import { RootState } from '../../store';
import { normalizeRole } from '../../routes/fieldAccess';

const actionPathFor = (notification: NotificationListItem, role?: string | null): string => {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === 'construction') return '/field/tasks';
  if (notification.notificationType === 'task_ready_for_inspection' && notification.relatedTaskId) return `/field/inspections/${notification.relatedTaskId}`;
  if (notification.notificationType === 'task_inspection_failed' && notification.relatedTaskId) return '/field/tasks';
  if (notification.notificationType === 'task_inspection_approved' && notification.relatedTaskId) return normalizedRole === 'supervisor' ? '/field/inspections' : '/field/performance';
  if (notification.notificationType === 'field_report_submitted') return '/field/pm/submitted-reports';
  return notification.relatedTaskId ? `/field/tasks/${notification.relatedTaskId}` : '/field';
};

const notificationHintFor = (notification: NotificationListItem) => {
  if (notification.notificationType === 'task_ready_for_inspection') {
    return notification.title.toLowerCase().includes('rework') ? 'Rework done' : 'New task needs inspecting';
  }
  if (notification.notificationType === 'task_inspection_failed') return 'Task needs rework';
  if (notification.notificationType === 'task_inspection_approved') return 'Task approved';
  if (notification.notificationType === 'field_report_submitted') return 'New field report submitted';
  if (notification.notificationType.includes('payment') || notification.notificationType.includes('transaction')) return 'Payment activity';
  if (notification.notificationType.includes('purchase_order') || notification.notificationType.includes('po')) return 'Purchase order activity';
  return notification.title;
};

const notificationTitle = (notification: NotificationListItem) => {
  const hint = notificationHintFor(notification);
  return notification.relatedTaskCode ? `${notification.relatedTaskCode} - ${hint}` : hint;
};

export default function NotificationsPage() {
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NotificationsCenter | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await notificationService.getCenter({ limit: 50 });
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle="Tasks and activity for your account"
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data || data.notifications.length === 0 ? (
        <Alert severity="info">No notifications right now.</Alert>
      ) : (
        <Stack spacing={1.25}>
          {data.notifications.map((notification) => (
            <Paper key={notification.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900 }}>{notificationTitle(notification)}</Typography>
                  <Typography variant="body2" color="text.secondary">{notification.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(notification.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button size="small" variant="contained" component={RouterLink} to={actionPathFor(notification, userRole)}>
                    Open
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
